import { Modal } from "react-bootstrap";
import { ServerRequestsSubscriptionGroupContractRuleDto } from "./shared/api/axios-client";
import { useMemo } from "react";

type Props = {
  show: boolean;
  onHide: () => void;
  contract: ServerRequestsSubscriptionGroupContractRuleDto[];
  groupName: string;
  /** Plan monthly unit price in cents — used to resolve final tier rates. */
  basePriceCents: number;
};

function formatCurrency(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

/** rule.price is signed adjustment (fee +, discount −); 0 ⇒ Free. */
function getFinalPriceCents(
  adjustment: number | null | undefined,
  _type: number | null | undefined,
  basePriceCents: number,
): number {
  if (adjustment == null || adjustment === 0) return 0;
  return Math.max(basePriceCents + adjustment, 0);
}

/** Plan monthly unit price in cents — not CachedMrr / subscription.price. */
export function resolvePlanBasePriceCents(
  prices: { planPriceId: number; price: number }[] | null | undefined,
  planPriceId: number | null | undefined,
  fallbackCents = 0,
): number {
  const match = prices?.find((p) => p.planPriceId === planPriceId);
  return match != null ? match.price : fallbackCents;
}

export default function ContractDetailsModal({
  show,
  onHide,
  contract,
  groupName,
  basePriceCents,
}: Props) {
  const sortedContract = useMemo(() => {
    return [...contract].sort((a, b) => {
      const aMin = a.min ?? Number.MAX_SAFE_INTEGER;
      const bMin = b.min ?? Number.MAX_SAFE_INTEGER;
      return aMin - bMin;
    });
  }, [contract]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Contract Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-3">
          <div className="text-muted small">
            The following pricing rules apply to <strong>{groupName}</strong>
          </div>
        </div>

        <div className="fw-semibold mb-2">Tier Rules</div>

        <div className="d-flex flex-column gap-2">
          {sortedContract.map((r, i) => {
            const finalCents = getFinalPriceCents(
              r.price,
              r.type,
              basePriceCents,
            );
            const isFree = finalCents === 0;

            return (
              <div
                key={i}
                className="d-flex align-items-center justify-content-between px-3 py-2 rounded border"
              >
                <div>
                  <div className="fw-medium">
                    {r.min ?? 0} – {r.max ?? "∞"} parkers
                  </div>

                  <div className="text-muted small">
                    {isFree ? "Free" : "Per parker / month"}
                  </div>
                </div>

                {isFree ? (
                  <span className="text-muted fw-semibold" style={{ fontSize: "1rem" }}>
                    Free
                  </span>
                ) : (
                  <span
                    className="fw-bold text-dark"
                    style={{ fontSize: "1.05rem", letterSpacing: "0.01em" }}
                  >
                    {formatCurrency(finalCents)}
                    <span className="fw-semibold text-muted ms-1" style={{ fontSize: "0.85rem" }}>
                      / mo
                    </span>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </Modal.Body>
    </Modal>
  );
}
